/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {
    SimpleGrid,
    Button,
    Center,
    useMultiStyleConfig,
    Box,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    ADD_FILTER,
    REMOVE_FILTER
} from '@salesforce/retail-react-app/app/pages/product-list/partials/refinements-utils'

const SizeRefinements = ({selectedFilterId, filter, toggleFilter, selectedFilters}) => {
    const {formatMessage} = useIntl()
    const styles = useMultiStyleConfig('SwatchGroup', {
        variant: 'square',
        disabled: false
    })

    return filter?.values?.map((valueFilter) => {
        // Find all available filter values
        const availableValues = valueFilter?.values || []
        const availableValueIds = new Set(availableValues.map((v) => v.value))

        // Find selected filters that are not in the available values list
        const unavailableSelectedFilters = selectedFilters.filter(
            (selectedValue) => !availableValueIds.has(selectedValue)
        )

        // Create dummy filter objects for unavailable selected filters
        const dummyFilters = unavailableSelectedFilters.map((value) => {
            // Try to find the original filter data from all filter values
            const originalValue = valueFilter?.values?.find((v) => v.value === value)
            return (
                originalValue || {
                    value: value,
                    label: value,
                    hitCount: 0
                }
            )
        })

        // Combine dummy filters with available filters
        const allValues = [...dummyFilters, ...availableValues]

        const filteredValues = allValues.filter((value) => {
            const isSelected = selectedFilters.includes(value.value)
            return value.hitCount > 0 || isSelected
        })

        if (!filteredValues?.length) return null

        return (
            <Box key={valueFilter.attributeId} mb={4}>
                <Text
                    mb={2}
                    color="grey.800"
                    fontSize="sm"
                    fontStyle="normal"
                    fontWeight="medium"
                    lineHeight="normal"
                >
                    {valueFilter.label}
                </Text>
                <SimpleGrid templateColumns="repeat(6, 1fr)" spacing={2}>
                    {filteredValues.map((value) => {
                        const isSelected =
                            selectedFilters.includes(value.value) &&
                            valueFilter.attributeId == selectedFilterId

                        return (
                            <Button
                                key={value.value}
                                {...styles.swatch}
                                {...styles.swatchButton}
                                m="0"
                                borderColor={isSelected ? 'black' : 'gray.200'}
                                backgroundColor={isSelected ? 'black' : 'white'}
                                color={isSelected ? 'white' : 'gray.900'}
                                padding={'4px 8px'}
                                height={'26px'}
                                w={'auto'}
                                onClick={() =>
                                    toggleFilter(value, valueFilter.attributeId, isSelected)
                                }
                                aria-checked={isSelected}
                                variant="outline"
                                aria-label={formatMessage(
                                    isSelected ? REMOVE_FILTER : ADD_FILTER,
                                    value
                                )}
                            >
                                <Center
                                    textAlign="center"
                                    fontSize="sm"
                                    fontStyle="normal"
                                    fontWeight="normal"
                                    lineHeight="normal"
                                >
                                    {value.label}
                                </Center>
                            </Button>
                        )
                    })}
                </SimpleGrid>
            </Box>
        )
    })
}

SizeRefinements.propTypes = {
    filter: PropTypes.object,
    selectedFilters: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
    toggleFilter: PropTypes.func,
    selectedFilterId: PropTypes.string
}

export default SizeRefinements
